<!-- Modal backdrop -->
<div
  class="bai-invite-backdrop"
  role="dialog"
  aria-modal="true"
  aria-labelledby="invite-modal-title"
  (click)="cancel()"
  (keydown.escape)="cancel()"
  tabindex="-1"
>
  <!-- Modal panel — stop click propagation so backdrop click doesn't fire inside -->
  <div class="bai-invite-panel" (click)="$event.stopPropagation(); $any($event.target).closest('[class*=__action]') && $any($event.currentTarget).removeAttribute('open')">

    <!-- Header -->
    <div class="bai-invite-header">
      <h2 id="invite-modal-title" class="bai-invite-title">
        <i class="pi pi-user-plus" aria-hidden="true"></i>
        Invite Financial Assistant
      </h2>
      <button
        type="button"
        class="bai-invite-close"
        (click)="cancel()"
        aria-label="Close invite modal"
      >
        <i class="pi pi-times" aria-hidden="true"></i>
      </button>
    </div>

    <!-- Search + filter row -->
    <div class="bai-invite-search-row">
      <div class="bai-invite-search-wrap">
        <i class="pi pi-search bai-invite-search-icon" aria-hidden="true"></i>
        <input
          type="search"
          class="bai-invite-search"
          placeholder="Search by name or email"
          [value]="searchQuery()"
          (input)="onSearchInput($any($event.target).value)"
          aria-label="Search financial assistants"
          autocomplete="off"
        />
      </div>
      <button
        type="button"
        class="bai-invite-filter-chip"
        [class.bai-invite-filter-chip--active]="filterAvailableOnly()"
        (click)="toggleAvailableFilter()"
        [attr.aria-pressed]="filterAvailableOnly()"
        aria-label="Show available only"
      >
        <span class="bai-invite__avail-dot bai-invite__avail-dot--available" aria-hidden="true"></span>
        Available now
      </button>
    </div>

    <!-- FA list -->
    <div
      class="bai-invite-list"
      role="radiogroup"
      aria-label="Select a financial assistant to invite"
    >
      @if (profiles().length === 0) {
        <div class="bai-invite-empty">
          <i class="pi pi-search" aria-hidden="true"></i>
          <span>No financial assistants match your search.</span>
        </div>
      }

      @for (fa of profiles(); track fa.id) {
        @let status = getInviteStatus(fa.id);
        @let isSelected = selectedFaId() === fa.id;
        @let isDisabled = status === 'pending' || status === 'accepted' || status === 'already_joined';

        <div
          class="bai-invite-row"
          [class.bai-invite-row--selected]="isSelected"
          [class.bai-invite-row--disabled]="isDisabled"
          role="radio"
          [attr.aria-checked]="isSelected"
          [attr.aria-disabled]="isDisabled"
          [attr.tabindex]="isDisabled ? -1 : 0"
          (click)="!isDisabled && selectFa(fa.id)"
          (keydown.space)="!isDisabled && selectFa(fa.id); $event.preventDefault()"
          (keydown.enter)="!isDisabled && selectFa(fa.id)"
        >
          <!-- Radio indicator -->
          <div class="bai-invite-radio" aria-hidden="true">
            @if (isSelected) {
              <i class="pi pi-circle-fill"></i>
            } @else {
              <i class="pi pi-circle"></i>
            }
          </div>

          <!-- Avatar -->
          <div class="bai-invite-avatar" aria-hidden="true">{{ fa.avatarInitials }}</div>

          <!-- Main info -->
          <div class="bai-invite-info">
            <div class="bai-invite-name-row">
              <span class="bai-invite-name">{{ fa.name }}</span>
              @if (fa.isCfp) {
                <span class="bai-invite-cfp-badge" pTooltip="Certified Financial Planner" tooltipPosition="top">CFP</span>
              }
              <span
                class="bai-invite-avail-badge"
                [class]="availabilityClass(fa.availability)"
              >
                <span class="bai-invite__avail-dot" [class]="availabilityClass(fa.availability) + '-dot'" aria-hidden="true"></span>
                {{ availabilityLabel(fa.availability) }}
              </span>
            </div>
            <div class="bai-invite-email">{{ fa.email }}</div>
            <div class="bai-invite-meta">
              <span class="bai-invite-role">Financial Assistant</span>
              <span class="bai-invite-sep" aria-hidden="true">·</span>
              <span class="bai-invite-tenure">{{ formatTenure(fa.tenureMonths) }}</span>
            </div>
            @if (fa.specialties.length > 0) {
              <div class="bai-invite-specialties" aria-label="Specialties">
                @for (s of fa.specialties; track s) {
                  <span class="bai-invite-specialty-chip">{{ specialtyLabel(s) }}</span>
                }
              </div>
            }
          </div>

          <!-- Status badge (right) -->
          @if (status === 'pending') {
            <span class="bai-invite-status-badge bai-invite-status-badge--pending">Invite pending</span>
          } @else if (status === 'accepted' || status === 'already_joined') {
            <span class="bai-invite-status-badge bai-invite-status-badge--joined">In conversation</span>
          }
        </div>
      }
    </div>

    <!-- Optional reason -->
    <div class="bai-invite-reason-section">
      <label for="invite-reason" class="bai-invite-reason-label">
        Invite reason <span class="bai-invite-optional">(optional)</span>
      </label>
      <textarea
        id="invite-reason"
        class="bai-invite-reason-input"
        [class.bai-invite-reason-input--error]="reasonTooLong()"
        placeholder="Add context for the invited FA…"
        rows="3"
        [value]="reason()"
        (input)="reason.set($any($event.target).value)"
        [attr.aria-describedby]="reasonTooLong() ? 'reason-error' : null"
        [attr.maxlength]="reasonMaxLength + 20"
      ></textarea>
      <div class="bai-invite-reason-footer">
        @if (reasonTooLong()) {
          <span id="reason-error" class="bai-invite-reason-error" role="alert">
            Reason must be {{ reasonMaxLength }} characters or fewer.
          </span>
        } @else {
          <span></span>
        }
        <span
          class="bai-invite-reason-count"
          [class.bai-invite-reason-count--over]="reasonTooLong()"
          aria-live="polite"
          [attr.aria-label]="reason().length + ' of ' + reasonMaxLength + ' characters'"
        >{{ reason().length }}/{{ reasonMaxLength }}</span>
      </div>
    </div>

    <!-- Footer actions -->
    <div class="bai-invite-footer">
      <button
        type="button"
        class="bai-invite-btn bai-invite-btn--cancel"
        (click)="cancel()"
      >Cancel</button>
      <button
        type="button"
        class="bai-invite-btn bai-invite-btn--send"
        [disabled]="!canSend() || reasonTooLong()"
        [attr.aria-disabled]="!canSend() || reasonTooLong()"
        (click)="send()"
        pTooltip="{{ !selectedFaId() ? 'Select a financial assistant first' : '' }}"
        tooltipPosition="top"
      >
        <i class="pi pi-send" aria-hidden="true"></i>
        Send Invite
      </button>
    </div>

  </div>
</div>
